import React, { MouseEvent, useCallback, useState } from "react";
import { TheEditHeader } from "web/react/components/the-edit-header";
import { ArchiveDeviceDivider } from "web/react/emo/archive-device-divider";
import * as styles from "./the-edit.css";

import clsx from "clsx";
import LoadingSpinner from "web/react/components/loading-spinner/loading-spinner";
import { PaginationProgressBar } from "web/react/components/pagination-progress-bar/pagination-progress-bar";
import { Button } from "web/react/emo/button";
import { Text } from "web/react/emo/text";
import { TheEditStoryCard } from "web/react/emo/the-edit-story-card";
import { VStack } from "web/react/emo/v-stack";
import { View } from "web/react/emo/view";
import { useDomViewport } from "web/react/hooks/use-dom-viewport/use-dom-viewport";
import { useRothkoFetcher } from "web/react/hooks/use-rothko-fetcher";
import analytics from "web/script/analytics/analytics";
import { gettext } from "web/script/modules/django-i18n";
import history from "web/script/utils/history";
import { TheEditSerializer } from "web/types/serializers";

function getNextPage(next_page): URL {
    const url = new URL(global.window.location.href);
    url.searchParams.set("page", next_page.toString());

    return url;
}

export function TheEdit({ story_cards, meta }: TheEditSerializer): React.ReactElement {
    const { isDomLoaded } = useDomViewport();
    const [items, setItems] = useState(story_cards);
    const [pageMeta, setPageMeta] = useState(meta);
    const [fetch, isLoading] = useRothkoFetcher("modules/the_edit/the_edit", undefined, true);

    const onButtonClick = useCallback(
        async function onButtonClick(
            event: MouseEvent<HTMLButtonElement> | MouseEvent<HTMLAnchorElement>
        ): Promise<void> {
            event.preventDefault();
            // don't fetch if there is no next page
            // we should never land here
            if (!pageMeta.next_page) {
                return;
            }

            analytics.event(
                "the_edit_show_more",
                "click_show_more_button",
                "click",
                false,
                {},
                "",
                {},
                [],
                false,
                false
            );

            const newContent = pageMeta.next_page
                ? await fetch({ page: pageMeta.next_page.toString() })
                : null;

            if (newContent && isDomLoaded) {
                // Before we update the state, set the url param with the
                // "next_page" we just used to fetch.
                // Then update URL without reloading.
                // (see media/web/script/components/lyst-app.js)
                const url = getNextPage(pageMeta.next_page);
                history.pushState(url, { saveScrollPosition: false });

                // add the new content to the already existing content
                setItems([...items, ...newContent["story_cards"]]);

                // update pagination details
                setPageMeta(newContent["meta"]);
            }
        },
        [items, pageMeta, fetch, isDomLoaded]
    );

    // "Showing {current_product_count} of {total_product_count}"
    const paginationText =
        pageMeta.total > 0 || items.length === 0
            ? gettext("feed_pagination.summary_showing_products", {
                  current_product_count: items.length,
                  total_product_count: pageMeta.total,
              })
            : "";

    const nextPageURL = isDomLoaded && pageMeta.next_page ? getNextPage(pageMeta.next_page) : null;

    return (
        <div className={styles.wrapper}>
            <div className={styles.container}>
                <TheEditHeader />
                <div className={styles.grid}>
                    {items.map((storyCardProps, i) => (
                        <TheEditStoryCard
                            key={`${storyCardProps.image_url}-${i}`}
                            index={i}
                            {...storyCardProps}
                        />
                    ))}
                </div>
            </div>
            {items.length > 0 ? (
                <VStack align="center" justify="center" spacing="none">
                    <View paddingBottom="xs">
                        <Text textStyle="body-s-bold">{paginationText}</Text>
                    </View>
                    <div className={styles.progressionWrapper}>
                        <PaginationProgressBar
                            currentNumber={items.length}
                            maximumNumber={pageMeta.total}
                        />
                    </div>
                    {nextPageURL && (
                        <Button
                            title={gettext("general.show_more")}
                            variant={"secondary"}
                            width={"full"}
                            onClick={onButtonClick}
                            href={nextPageURL.href}
                        />
                    )}
                    <div className={clsx(styles.loadingWrapper, styles.inlineLoadingWrapper)}>
                        {isLoading && <LoadingSpinner />}
                    </div>
                </VStack>
            ) : (
                gettext("retailer_promo_profile.feed_empty_text")
            )}
            <ArchiveDeviceDivider />
        </div>
    );
}
